import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import type {
    AccessData,
    AccountImage,
    Customer,
    CustomerLoyaltyProgramStatsItem,
    CustomerOrder,
    CustomersFeedbackReportItem,
    InventoryItem,
    LoyaltyProgramData,
    Setting,
    UserAccount,
} from "../components/data/types"
import type { ObjectOfVersions } from "../components/operations/reducer"
import type { InventoryTransactionLog } from "../components/screens/inventory/components/LogOfChangesTable"
import type { OrderReportItem } from "../components/screens/reports/orders/types"
import type {
    CashReportItem,
    CustomerReportItem,
    OrderDetail,
    PreparationTimeByItemReport,
    PreparationTimeHourlyReport,
    SummaryNetCollectedBreakdownByEmployeeReportItem,
    SummaryNetCollectedBreakdownReportItem,
    SummaryNetCollectedByEmployeeReportItem,
    SummaryNetCollectedBySourceReportItem,
    SummaryNetRevenueByAssignmentReportItem,
    SummaryNetRevenueByCategoryReportItem,
    SummaryTransactionsByPaymentTypeReportItem,
} from "../graphql/types"
import {
    SummaryNetCollectedBreakdownByPaymentTypeReportItem,
    TipsAndGratuityByAssignmentReportItem,
} from "../graphql/types"
import { BreakdownByHourItem, TrendingDataItem } from "../components/screens/reports/summary/types"
import { DepartmentDTO, RoleDTO } from "../components/screens/employees/positions/types"
import { MenuImportType } from "../events/MenuImport/types"

// Account Images

// todo: refactor and remove - this is only used for MIB location picker (appearance) images
export const SAVE_ACCOUNT_IMAGE_REQUESTED = "save/account/img/requested"
export const saveAccountImageRequested = (
    file: Maybe<any>,
    type: string,
    terminalId?: Maybe<string>,
    id?: Maybe<string>,
    sortIndex?: Maybe<number>,
    label?: Maybe<string>
) => ({
    type: SAVE_ACCOUNT_IMAGE_REQUESTED,
    payload: {
        file: file,
        type: type,
        id: id || null,
        terminalId: terminalId || null,
        sortIndex: sortIndex,
        label: label,
    },
})

export const SAVE_ACCOUNT_IMAGES_SUCCESS = "save/account/imgs/success"
export const saveAccountImagesSuccess = (accountImages: AccountImage[], versions: ObjectOfVersions) => ({
    type: SAVE_ACCOUNT_IMAGES_SUCCESS,
    payload: {
        accountImages: accountImages,
        versions: versions,
    },
})

export const SAVE_ACCOUNT_IMAGE_FAILURE = "save/account/img/failure"
export const saveAccountImageFailure = (error: any) => ({
    type: SAVE_ACCOUNT_IMAGE_FAILURE,
    payload: {
        error: error,
    },
})

export const FETCH_ACCOUNT_IMAGES_SUCCESS = "fetch/accountImages/success"
export const fetchAccountImagesSuccess = (data: AccountImage[], versions: ObjectOfVersions) => ({
    type: FETCH_ACCOUNT_IMAGES_SUCCESS,
    payload: {
        data: data,
        versions: versions,
    },
})

export const FETCH_INVENTORY_LOG_OF_CHANGES_REQUESTED = "fetch/inventoryItems/logs/requested"
export const fetchInventoryLogOfChangesRequested = (inventoryItemId: string, locationId: Maybe<string>) => ({
    type: FETCH_INVENTORY_LOG_OF_CHANGES_REQUESTED,
    payload: {
        inventoryItemId: inventoryItemId,
        locationId: locationId,
    },
})

export const FETCH_INVENTORY_LOG_OF_CHANGES_SUCCESS = "fetch/inventory/logs/success"
export const fetchInventoryLogOfChangesSuccess = (data: InventoryTransactionLog[]) => ({
    type: FETCH_INVENTORY_LOG_OF_CHANGES_SUCCESS,
    payload: {
        data: data,
    },
})

export const FETCH_INVENTORY_ITEMS_SUCCESS = "fetch/inventoryItems/success"
export const fetchInventoryItemsSuccess = (data: InventoryItem[], versions: ObjectOfVersions) => ({
    type: FETCH_INVENTORY_ITEMS_SUCCESS,
    payload: {
        data: data,
        versions: versions,
    },
})

export const SEND_CSV_FILE_REQUESTED = "send/csv/file/requested"

export const sendCsvFileRequested = (type: MenuImportType, file: File) => ({
    type: SEND_CSV_FILE_REQUESTED,
    payload: {
        file: file,
        type: type,
    },
})

export const SEND_CSV_FILE_SUCCESS = "send/csv/file/success"

export const sendCsvFileSuccess = (message: Maybe<string>) => ({
    type: SEND_CSV_FILE_SUCCESS,
    payload: {
        message: message,
    },
})

export const SEND_CSV_FILE_FAILURE = "send/csv/file/failure"

export const sendCsvFileFailure = (message: Maybe<string>) => ({
    type: SEND_CSV_FILE_FAILURE,
    payload: {
        message: message,
    },
})

export const FETCH_CUSTOMER_LOYALTY_STATS_REQUESTED = "customer-loyalty-stats/requested"

export const fetchCustomerLoyaltyStatsRequested = (loyaltyProgramId: string, customerId: string) => ({
    type: FETCH_CUSTOMER_LOYALTY_STATS_REQUESTED,
    payload: {
        loyaltyProgramId: loyaltyProgramId,
        customerId: customerId,
    },
})

export const FETCH_CUSTOMER_LOYALTY_STATS_SUCCESS = "customer-loyalty-stats/success"

export const fetchCustomerLoyaltyStatsSuccess = (stats: CustomerLoyaltyProgramStatsItem) => ({
    type: FETCH_CUSTOMER_LOYALTY_STATS_SUCCESS,
    payload: {
        stats: stats,
    },
})

export const FETCH_LOYALTY_PROGRAM_REQUESTED = "loyalty-program/requested"

export const fetchLoyaltyProgramRequested = () => ({
    type: FETCH_LOYALTY_PROGRAM_REQUESTED,
})

export const FETCH_LOYALTY_PROGRAM_SUCCESS = "loyalty-program/success"

export const fetchLoyaltyProgramSuccess = (data: LoyaltyProgramData[], versions: ObjectOfVersions) => ({
    type: FETCH_LOYALTY_PROGRAM_SUCCESS,
    payload: {
        data: data,
        versions: versions,
    },
})

export const FETCH_LOYALTY_PROGRAM_FAILURE = "loyalty-program/failure"

export const fetchLoyaltyProgramFailure = (error: any) => ({
    type: FETCH_LOYALTY_PROGRAM_FAILURE,
    payload: {
        error: error,
    },
})

export const FETCH_SETTINGS_SUCCESS = "settings/success"
export const fetchSettingsSuccess = (data: Setting[]) => ({
    type: FETCH_SETTINGS_SUCCESS,
    payload: {
        data: data,
    },
})

export const FETCH_TIMESHEET_REPORT_REQUESTED = "timesheet/requested"
export const fetchTimesheetReportRequested = (userAccountId?: Maybe<string>) => ({
    type: FETCH_TIMESHEET_REPORT_REQUESTED,
    payload: {
        userAccountId: userAccountId,
    },
})

export const FETCH_CASH_REPORT_REQUESTED = "fetchCashReport/requested"
export const fetchCashReportRequested = () => ({
    type: FETCH_CASH_REPORT_REQUESTED,
})

export const FETCH_CASH_REPORT_SUCCESS = "fetchCashReport/success"
export const fetchCashReportSuccess = (data: CashReportItem[], currentPage: number) => ({
    type: FETCH_CASH_REPORT_SUCCESS,
    payload: {
        currentPage: currentPage,
        data: data,
    },
})

export const FETCH_CASH_REPORT_FAILURE = "fetchCashReport/failure"
export const fetchCashReportFailure = (error?: any) => ({
    type: FETCH_CASH_REPORT_FAILURE,
    payload: {
        error: error,
    },
})

export const FETCH_PREPARATION_TIME_BY_ITEM_REPORT_REQUESTED = "preparationTimeByItem/requested"
export const fetchPreparationTimeByItemReportRequested = () => ({
    type: FETCH_PREPARATION_TIME_BY_ITEM_REPORT_REQUESTED,
})

export const FETCH_PREPARATION_TIME_BY_ITEM_REPORT_SUCCESS = "FETCH_PREPARATION_TIME_BY_ITEM_REPORT_SUCCESS"
export const createActionFetchPreparationTimeByItemReportSuccess = (data: PreparationTimeByItemReport) => ({
    type: FETCH_PREPARATION_TIME_BY_ITEM_REPORT_SUCCESS,
    payload: {
        data: data,
    },
})

export const FETCH_PREPARATION_TIME_BY_ITEM_REPORT_FAILURE = "FETCH_PREPARATION_TIME_BY_ITEM_REPORT_FAILURE"
export const createActionFetchPreparationTimeByItemReportFailure = (error: any) => ({
    type: FETCH_PREPARATION_TIME_BY_ITEM_REPORT_FAILURE,
    payload: {
        error: error,
    },
})

export const FETCH_PREPARATION_TIME_HOURLY_REPORT_REQUESTED = "preparationTimeHourly/requested"
export const fetchPreparationTimeHourlyReportRequested = () => ({
    type: FETCH_PREPARATION_TIME_HOURLY_REPORT_REQUESTED,
})

export const FETCH_PREPARATION_TIME_HOURLY_REPORT_SUCCESS = "FETCH_PREPARATION_TIME_HOURLY_REPORT_SUCCESS"
export const createActionFetchPreparationTimeHourlyReportSuccess = (data: PreparationTimeHourlyReport) => ({
    type: FETCH_PREPARATION_TIME_HOURLY_REPORT_SUCCESS,
    payload: {
        data: data,
    },
})

export const FETCH_PREPARATION_TIME_HOURLY_REPORT_FAILURE = "FETCH_PREPARATION_TIME_HOURLY_REPORT_FAILURE"
export const createActionFetchPreparationTimeHourlyReportFailure = (error: any) => ({
    type: FETCH_PREPARATION_TIME_HOURLY_REPORT_FAILURE,
    payload: {
        error: error,
    },
})

export const FETCH_ORDER_DETAIL_REQUESTED = "fetchOrderDetail/requested"
export const fetchOrderDetailRequested = (orderId: string) => ({
    type: FETCH_ORDER_DETAIL_REQUESTED,
    payload: {
        orderId: orderId,
    },
})

export const FETCH_ORDER_DETAIL_SUCCESS = "fetchOrderDetail/success"
export const fetchOrderDetailSuccess = (orderDetail: OrderDetail) => ({
    type: FETCH_ORDER_DETAIL_SUCCESS,
    payload: {
        orderDetail: orderDetail,
    },
})

export const FETCH_ORDER_DETAIL_FAILURE = "fetchOrderDetail/failure"
export const fetchOrderDetailFailure = (error: any) => ({
    type: FETCH_ORDER_DETAIL_FAILURE,
    payload: {
        error: error,
    },
})

export const FETCH_ORDERS_REPORT_REQUESTED = "fetchOrdersReport/requested"

export const fetchOrdersReportRequested = () => ({
    type: FETCH_ORDERS_REPORT_REQUESTED,
})

export const FETCH_ORDERS_REPORT_SUCCESS = "fetchOrdersReport/success"

export const fetchOrdersReportSuccess = (data: OrderReportItem[], currentPage: number) => ({
    type: FETCH_ORDERS_REPORT_SUCCESS,
    payload: {
        currentPage: currentPage,
        data: data,
    },
})

export const FETCH_ORDERS_REPORT_FAILURE = "fetchOrdersReport/failure"

export const fetchOrdersReportFailure = (error?: any) => ({
    type: FETCH_ORDERS_REPORT_FAILURE,
    payload: {
        error: error,
    },
})

export const FETCH_CUSTOMER_ORDERS_REQUESTED = "fetchCustomerOrders/requested"

export const fetchCustomerOrdersRequested = (customerId: string) => ({
    type: FETCH_CUSTOMER_ORDERS_REQUESTED,
    payload: {
        customerId: customerId,
    },
})

export const FETCH_CUSTOMER_ORDERS_SUCCESS = "fetchCustomerOrders/success"

export const fetchCustomerOrdersSuccess = (data: CustomerOrder[]) => ({
    type: FETCH_CUSTOMER_ORDERS_SUCCESS,
    payload: {
        data: data,
    },
})

export const FETCH_CUSTOMER_ORDERS_FAILURE = "fetchCustomerOrders/failure"

export const fetchCustomerOrdersFailure = (error: any) => ({
    type: FETCH_CUSTOMER_ORDERS_FAILURE,
    payload: {
        error: error,
    },
})

export const FETCH_CUSTOMER_DETAIL_REQUESTED = "fetchCustomersDetail/requested"

export const fetchCustomerDetailRequested = (customerId: string) => ({
    type: FETCH_CUSTOMER_DETAIL_REQUESTED,
    payload: {
        customerId: customerId,
    },
})

export const FETCH_CUSTOMER_DETAIL_SUCCESS = "fetchCustomersDetail/success"

export const fetchCustomerDetailSuccess = (data: Customer[], versions: ObjectOfVersions) => ({
    type: FETCH_CUSTOMER_DETAIL_SUCCESS,
    payload: {
        data: data,
        versions: versions,
    },
})

export const FETCH_CUSTOMER_DETAIL_FAILURE = "fetchCustomersDetail/failure"

export const fetchCustomerDetailFailure = (error: any) => ({
    type: FETCH_CUSTOMER_DETAIL_FAILURE,
    payload: {
        error: error,
    },
})

export const FETCH_CUSTOMERS_REPORT_REQUESTED = "fetchCustomersReport/requested"
export const fetchCustomersReportRequested = () => ({
    type: FETCH_CUSTOMERS_REPORT_REQUESTED,
})

export const FETCH_CUSTOMERS_REPORT_SUCCESS = "fetchCustomersReport/success"

export const fetchCustomersReportSuccess = (data: CustomerReportItem[], currentPage: number) => ({
    type: FETCH_CUSTOMERS_REPORT_SUCCESS,
    payload: {
        currentPage: currentPage,
        data: data,
    },
})

export const FETCH_CUSTOMERS_FEEDBACK_REPORT_REQUESTED = "fetchCustomersFeedbackReport/requested"

export const fetchCustomersFeedbackReportRequested = () => ({
    type: FETCH_CUSTOMERS_FEEDBACK_REPORT_REQUESTED,
})

export const FETCH_CUSTOMERS_FEEDBACK_REPORT_SUCCESS = "fetchCustomersFeedbackReport/success"

export const fetchCustomersFeedbackReportSuccess = (data: CustomersFeedbackReportItem[], currentPage: number) => ({
    type: FETCH_CUSTOMERS_FEEDBACK_REPORT_SUCCESS,
    payload: {
        currentPage: currentPage,
        data: data,
    },
})

export const FETCH_CUSTOMERS_FEEDBACK_REPORT_FAILURE = "fetchCustomersFeedbackReport/failure"

export const fetchCustomersFeedbackReportFailure = (error: any) => ({
    type: FETCH_CUSTOMERS_FEEDBACK_REPORT_FAILURE,
    payload: {
        error: error,
    },
})

export const FETCH_NET_COLLECTED_BREAKDOWN_DATA_SUCCESS = "FETCH_NET_COLLECTED_BREAKDOWN_DATA_SUCCESS"

export const fetchNetCollectedBreakdownDataSuccess = (data: SummaryNetCollectedBreakdownReportItem) => ({
    type: FETCH_NET_COLLECTED_BREAKDOWN_DATA_SUCCESS,
    payload: {
        data: data,
    },
})

export const FETCH_NET_COLLECTED_BREAKDOWN_DATA_FAILURE = "FETCH_NET_COLLECTED_BREAKDOWN_DATA_FAILURE"

export const fetchNetCollectedBreakdownDataFailure = (error?: any) => ({
    type: FETCH_NET_COLLECTED_BREAKDOWN_DATA_FAILURE,
    payload: {
        error: error,
    },
})

export const FETCH_NET_COLLECTED_BREAKDOWN_BY_PAYMENT_TYPE_DATA_SUCCESS =
    "FETCH_NET_COLLECTED_BREAKDOWN_BY_PAYMENT_TYPE_DATA_SUCCESS"
export const fetchNetCollectedBreakdownByPaymentTypeDataSuccess = (
    data: SummaryNetCollectedBreakdownByPaymentTypeReportItem
) => ({
    type: FETCH_NET_COLLECTED_BREAKDOWN_BY_PAYMENT_TYPE_DATA_SUCCESS,
    payload: {
        data: data,
    },
})

export const FETCH_NET_COLLECTED_BREAKDOWN_BY_PAYMENT_TYPE_DATA_FAILURE =
    "FETCH_NET_COLLECTED_BY_PAYMENT_TYPE_BREAKDOWN_DATA_FAILURE"

export const fetchNetCollectedBreakdownByPaymentTypeDataFailure = (error?: any) => ({
    type: FETCH_NET_COLLECTED_BREAKDOWN_BY_PAYMENT_TYPE_DATA_FAILURE,
    payload: {
        error: error,
    },
})

export const FETCH_TIPS_AND_GRATUITY_BY_ASSIGNMENT_DATA_SUCCESS = "FETCH_TIPS_AND_GRATUITY_BY_ASSIGNMENT_DATA_SUCCESS"
export const fetchTipsAndGratuityByAssignmentDataSuccess = (data: TipsAndGratuityByAssignmentReportItem[]) => ({
    type: FETCH_TIPS_AND_GRATUITY_BY_ASSIGNMENT_DATA_SUCCESS,
    payload: {
        data: data,
    },
})

export const FETCH_NET_COLLECTED_BY_EMPLOYEE_DATA_SUCCESS = "FETCH_NET_COLLECTED_BY_EMPLOYEE_DATA_SUCCESS"

export const fetchNetCollectedByEmployeeDataSuccess = (data: SummaryNetCollectedByEmployeeReportItem[]) => ({
    type: FETCH_NET_COLLECTED_BY_EMPLOYEE_DATA_SUCCESS,
    payload: {
        data: data,
    },
})

export const FETCH_NET_COLLECTED_BY_EMPLOYEE_DATA_FAILURE = "FETCH_NET_COLLECTED_BY_EMPLOYEE_DATA_FAILURE"

export const fetchNetCollectedByEmployeeDataFailure = (error?: any) => ({
    type: FETCH_NET_COLLECTED_BY_EMPLOYEE_DATA_FAILURE,
    payload: {
        error: error,
    },
})

export const FETCH_NET_COLLECTED_BY_EMPLOYEE_DETAIL_DATA_SUCCESS = "FETCH_NET_COLLECTED_BY_EMPLOYEE_DETAIL_DATA_SUCCESS"

export const fetchNetCollectedByEmployeeDetailDataSuccess = (
    data: SummaryNetCollectedBreakdownByEmployeeReportItem[]
) => ({
    type: FETCH_NET_COLLECTED_BY_EMPLOYEE_DETAIL_DATA_SUCCESS,
    payload: {
        data: data,
    },
})

export const FETCH_NET_COLLECTED_BY_EMPLOYEE_DETAIL_DATA_FAILURE = "FETCH_NET_COLLECTED_BY_EMPLOYEE_DETAIL_DATA_FAILURE"

export const fetchNetCollectedByEmployeeDetailDataFailure = (error?: any) => ({
    type: FETCH_NET_COLLECTED_BY_EMPLOYEE_DETAIL_DATA_FAILURE,
    payload: {
        error: error,
    },
})

export const FETCH_NET_REVENUE_BY_ASSIGNMENT_DATA_SUCCESS = "FETCH_NET_REVENUE_BY_ASSIGNMENT_DATA_SUCCESS"

export const fetchNetRevenueByAssignmentDataSuccess = (data: SummaryNetRevenueByAssignmentReportItem[]) => ({
    type: FETCH_NET_REVENUE_BY_ASSIGNMENT_DATA_SUCCESS,
    payload: {
        data: data,
    },
})

export const FETCH_NET_COLLECTED_BY_SOURCE_DATA_SUCCESS = "FETCH_NET_COLLECTED_BY_SOURCE_DATA_SUCCESS"

export const fetchNetCollectedBySourceDataSuccess = (data: SummaryNetCollectedBySourceReportItem[]) => ({
    type: FETCH_NET_COLLECTED_BY_SOURCE_DATA_SUCCESS,
    payload: {
        data: data,
    },
})

export const FETCH_NET_COLLECTED_BY_SOURCE_DATA_FAILURE = "FETCH_NET_COLLECTED_BY_SOURCE_DATA_FAILURE"

export const fetchNetCollectedBySourceDataFailure = (error: any) => ({
    type: FETCH_NET_COLLECTED_BY_SOURCE_DATA_FAILURE,
    payload: {
        error: error,
    },
})

export const FETCH_NET_REVENUE_BY_CATEGORY_DATA_SUCCESS = "FETCH_NET_REVENUE_BY_CATEGORY_DATA_SUCCESS"

export const fetchNetRevenueByCategoryDataSuccess = (data: SummaryNetRevenueByCategoryReportItem[]) => ({
    type: FETCH_NET_REVENUE_BY_CATEGORY_DATA_SUCCESS,
    payload: {
        data: data,
    },
})

export const FETCH_NET_REVENUE_BY_CATEGORY_DATA_FAILURE = "FETCH_NET_REVENUE_BY_CATEGORY_DATA_FAILURE"

export const fetchNetRevenueByCategoryDataFailure = (error?: any) => ({
    type: FETCH_NET_REVENUE_BY_CATEGORY_DATA_FAILURE,
    payload: {
        error: error,
    },
})

export const FETCH_TRANSACTIONS_BY_PAYMENT_TYPE_DATA_SUCCESS = "FETCH_TRANSACTIONS_BY_PAYMENT_TYPE_DATA_SUCCESS"

export const fetchTransactionsByPaymentTypeDataSuccess = (data: SummaryTransactionsByPaymentTypeReportItem[]) => ({
    type: FETCH_TRANSACTIONS_BY_PAYMENT_TYPE_DATA_SUCCESS,
    payload: {
        data: data,
    },
})

export const FETCH_TRANSACTIONS_BY_PAYMENT_TYPE_DATA_FAILURE = "FETCH_TRANSACTIONS_BY_PAYMENT_TYPE_DATA_FAILURE"

export const fetchTransactionsByPaymentTypeDataFailure = (error?: any) => ({
    type: FETCH_TRANSACTIONS_BY_PAYMENT_TYPE_DATA_FAILURE,
    payload: {
        error: error,
    },
})

// User Account
export const FETCH_USER_ACCOUNTS_SUCCESS = "fetchUserAccounts/success"

export const fetchUserAccountsSuccess = (userAccounts: UserAccount[], versions: ObjectOfVersions) => ({
    type: FETCH_USER_ACCOUNTS_SUCCESS,
    payload: {
        userAccounts: userAccounts,
        versions: versions,
    },
})

export const FETCH_USER_ACCOUNT_SUCCESS = "fetchUserAccount/success"

export const fetchUserAccountSuccess = (userAccount: UserAccount) => ({
    type: FETCH_USER_ACCOUNT_SUCCESS,
    payload: {
        userAccount: userAccount,
    },
})

export const FETCH_USER_ACCOUNTS_FAILURE = "fetchUserAccount/failure"

export const fetchUserAccountsFailure = (error: any) => ({
    type: FETCH_USER_ACCOUNTS_FAILURE,
    payload: {
        error: error,
    },
})
// Roles

export const FETCH_ROLES_SUCCESS = "FETCH_ROLES_SUCCESS"

export const fetchRolesSuccess = (roles: RoleDTO[], versions: ObjectOfVersions) => ({
    type: FETCH_ROLES_SUCCESS,
    payload: {
        roles: roles,
        versions: versions,
    },
})

export const FETCH_ROLES_FAILURE = "FETCH_ROLES_FAILURE"

export const fetchRolesFailure = (error: any) => ({
    type: FETCH_ROLES_FAILURE,
    payload: {
        error: error,
    },
})
// Departments

export const FETCH_DEPARTMENTS_SUCCESS = "FETCH_DEPARTMENTS_SUCCESS"

export const fetchDepartmentsSuccess = (departments: DepartmentDTO[], versions: ObjectOfVersions) => ({
    type: FETCH_DEPARTMENTS_SUCCESS,
    payload: {
        departments: departments,
        versions: versions,
    },
})

export const FETCH_DEPARTMENTS_FAILURE = "FETCH_DEPARTMENTS_FAILURE"

export const fetchDepartmentsFailure = (error: any) => ({
    type: FETCH_DEPARTMENTS_FAILURE,
    payload: {
        error: error,
    },
})
// Access and Roles

export const FETCH_ACCESS_AND_ROLES_SUCCESS = "FETCH_ACCESS_AND_ROLES_SUCCESS"

export const fetchAccessAndRolesSuccess = (accesses: AccessData[], versions: ObjectOfVersions) => ({
    type: FETCH_ACCESS_AND_ROLES_SUCCESS,
    payload: {
        accesses: accesses,
        versions: versions,
    },
})

export const FETCH_ACCESS_AND_ROLES_FAILURE = "FETCH_ACCESS_AND_ROLES_FAILURE"

export const fetchAccessAndRolesFailure = (error: any) => ({
    type: FETCH_ACCESS_AND_ROLES_FAILURE,
    payload: {
        error: error,
    },
})

export const FETCH_TRENDING_DATA_SUCCESS = "FETCH_TRENDING_DATA_SUCCESS"

export const fetchTrendingDataSuccess = (data: TrendingDataItem[]) => ({
    type: FETCH_TRENDING_DATA_SUCCESS,
    payload: {
        data: data,
    },
})

export const FETCH_TRENDING_DATA_FAILURE = "FETCH_TRENDING_DATA_FAILURE"

export const fetchTrendingDataFailure = (error: any) => ({
    type: FETCH_TRENDING_DATA_FAILURE,
    payload: {
        error: error,
    },
})

export const FETCH_BREAKDOWN_BY_HOUR_DATA_SUCCESS = "FETCH_BREAKDOWN_BY_HOUR_DATA_SUCCESS"

export const fetchBreakdownByHourDataSuccess = (data: BreakdownByHourItem[]) => ({
    type: FETCH_BREAKDOWN_BY_HOUR_DATA_SUCCESS,
    payload: {
        data: data,
    },
})

export const FETCH_BREAKDOWN_BY_HOUR_DATA_FAILURE = "FETCH_BREAKDOWN_BY_HOUR_DATA_FAILURE"

export const fetchBreakdownByHourDataFailure = (error: any) => ({
    type: FETCH_BREAKDOWN_BY_HOUR_DATA_FAILURE,
    payload: {
        error: error,
    },
})
